import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateUserProfileApi } from "../api/api";
import { LOGIN_SUCCESS, LOGOUT_SUCCESS } from "../redux/actions/action";
import { handleError } from "../helpers/handleError";
import { toast } from "react-toastify";
import "./DniPromptModal.css";

const sanitizeDni = (value) => String(value || "").replace(/\D+/g, "").slice(0, 10);

const isEmptyDni = (value) => {
  if (value == null) return true;
  const cleaned = sanitizeDni(value);
  return cleaned.length === 0;
};

const DniPromptModal = ({ currentPathname = "" }) => {
  const auth = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dni, setDni] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const userRole = auth?.user?.role;

  const needsDni = useMemo(() => {
    if (!auth?.isAuthenticated) return false;
    // Solo los compradores (rol USER) requieren DNI para completar checkout.
    // Staff (SELLER/SCANNER) y ORGANIZER no deben ser bloqueados por este modal.
    if (String(userRole).toUpperCase() !== "USER") return false;
    return isEmptyDni(auth?.user?.dni);
  }, [auth?.isAuthenticated, auth?.user?.dni, userRole]);

  useEffect(() => {
    if (!needsDni || !auth?.accessToken) {
      setOpen(false);
      return;
    }

    // /entrar tampoco: ahí se canjea el pase de Zoco, y la persona tiene que poder leer adónde
    // sigue (por ejemplo, que su rol todavía no entra al panel) sin un modal encima.
    const blockedRoutes = ["/complete-profile", "/register", "/login", "/entrar"];
    if (
      blockedRoutes.some((prefix) => currentPathname.startsWith(prefix)) ||
      String(userRole).toUpperCase() !== "USER"
    ) {
      setOpen(false);
      return;
    }

    setDni(sanitizeDni(auth?.user?.dni || ""));
    setError("");
    setOpen(true);
  }, [needsDni, auth?.accessToken, auth?.user?.dni, currentPathname, userRole]);

  const validate = (value) => {
    const cleaned = sanitizeDni(value);
    if (cleaned.length < 7 || cleaned.length > 10) {
      return "Ingresa un DNI valido (7 a 10 digitos).";
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate(dni);
    if (validation) {
      setError(validation);
      return;
    }

    if (!auth?.accessToken) return;
    const cleaned = sanitizeDni(dni);
    try {
      setSaving(true);
      setError("");
      const payload = { dni: cleaned };
      const res = await updateUserProfileApi(auth.accessToken, payload);
      const updatedUser =
        res?.data?.info?.user || { ...auth.user, ...payload };

      dispatch({
        type: LOGIN_SUCCESS,
        payload: {
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          user: updatedUser,
        },
      });

      try {
        toast.success("Listo, guardamos tu DNI.");
      } catch (_) {}

      setOpen(false);
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    dispatch({ type: LOGOUT_SUCCESS });
    setOpen(false);
    try {
      toast.info("Cerraste sesión.");
    } catch (_) {}
    navigate("/login", { replace: true });
  };

  if (!open) return null;

  return (
    <Modal
      backdrop="static"
      keyboard={false}
      centered
      show={open}
      onHide={() => {}}
      className="dni-prompt-modal"
    >
      <Modal.Header closeButton={false}>
        <Modal.Title>Completa tu DNI</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="dni-modal-subtitle">
          Para mantener tus tickets seguros necesitamos tu DNI. Esta informacion
          ayuda a validar tu identidad y prevenir fraudes.
        </p>
        <Form id="dni-modal-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="dniPromptField">
            <Form.Label>DNI</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              autoFocus
              placeholder="Ingresa tu DNI"
              value={dni}
              onInput={(e) => {
                const cleaned = sanitizeDni(e.target.value);
                e.target.value = cleaned;
                setDni(cleaned);
              }}
              onChange={(e) => setDni(sanitizeDni(e.target.value))}
              disabled={saving}
            />
            {error && <div className="field-error mt-2">{error}</div>}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="outline-secondary"
          className="dni-logout-btn"
          onClick={handleLogout}
          disabled={saving}
        >
          Cerrar sesión
        </Button>
        <Button
          form="dni-modal-form"
          type="submit"
          variant="primary"
          className="dni-submit-btn"
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Guardando...
            </>
          ) : (
            "Guardar DNI"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DniPromptModal;
